
import { v4 as uuidv4 } from "uuid";

import { 
  FeatureSource,
} from "@models";
import { 
  CharacterFeatureBase,
  Character,
  MagicItemKeyword,
  Race,
  Subrace,
  GameClass,
  Subclass,
  Gift,
  Item,
  CharacterASIBaseFeature,
  CharacterASIFeature,
  SuggestedSpells,
  CharacterSpellbook,
  ExtraSpellLists,
  CharacterFeature,
  CharacterSpell,
  CharacterBonusSpells,
  StandardFeatures,
  TableBase,
  ComplexCreatureInstance,
} from "@dndModels";
import { AbilityScoreType, SelectableType } from "@dndModels/Types";

import DataUtilities from "@dndUtilities/data_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const data_util = DataUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export class CharacterFeatureSource {
  // To distinguish between different instances of the same FeatureSource 
  // on the same character.  
  // This will allow me to implement repeatable Feats.
  true_id = ""; 
  source_id = "";
  source_type = "";
  source_name = "";
  // root_id: string = "";
  // root_type: string = "";
  // root_name: string = "";
  ancestries: FeatureAncestry[] = [];
  item_id = "";
  item_name = "";
  parent_feature_id = "";
  parent_feature_base_id = "";
  parent_feature_base_level = -1;
  parent_cfb?: CharacterFeatureBase;
  parent_cf?: CharacterFeature;
  source: FeatureSource | StandardFeatures | null = null;
  char_feature_bases: CharacterFeatureBase[] = [];
  use_char_feature_bases: CharacterFeatureBase[] = []; // This holds a subset of the one above, just holding the ones that are actually in use
  sub_char_feature_sources: CharacterFeatureSource[] = [];
  selectable_hash: { [key: string]: string[] } = {};
  selectable_types: { [key: string]: SelectableType } = {};
  name_bonuses: string[] = [];
  replace_name = "";
  suggested_spells: SuggestedSpells[] = [];
  table: TableBase | undefined = undefined;
  spellcasting: CFSSpellcasting = new CFSSpellcasting();
  needs_attention = false;
  level = 1;
  do_suggestions = false;

  notes = ""; // Populated from fs in char_util so it can get it from original if it's a variant and the variant's notes are empty.

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id && obj.true_id !== "" ? obj.true_id : uuidv4().toString();
    if (this.true_id === "") {
      this.true_id = uuidv4().toString();
    }
    if (obj) {
      this.source_id = obj.source_id ? obj.source_id : "";
      this.source_type = obj.source_type ? obj.source_type : "";
      this.source_name = obj.source_name ? obj.source_name : "";
      this.level = obj.level ? obj.level : 1;
      this.do_suggestions = obj.do_suggestions ? obj.do_suggestions : false;
      
      this.ancestries = obj.ancestries ? obj.ancestries : [];

      if (obj.char_feature_bases && obj.char_feature_bases.length > 0) {
        // if (this.true_id === "7f282d9c-8f28-4b7d-8346-e02069920a1b" || this.source_id === "66db36d839da3dfad811acc8") {
        //   debugger;
        // }
        for (const o of obj.char_feature_bases) {
          if (this.char_feature_bases.filter(o2 => o2.true_id === o.true_id).length === 0) {
            const fb = new CharacterFeatureBase(o); //, this.true_id === "7f282d9c-8f28-4b7d-8346-e02069920a1b" && o.level === 8);
            // if (obj.root_name === "Tabaxi") {
            //   console.log(o);
            //   console.log(fb);
            // }
            fb.source_id = this.source_id;
            fb.source_type = this.source_type;
            fb.source_name = this.get_use_name(); // .source_name;
            // if (this.root_id !== "") {
            //   fb.root_id = this.root_id;
            //   fb.root_type = this.root_type;
            //   fb.root_name = this.root_name;
            // } else {
            //   fb.root_id = this.source_id;
            //   fb.root_type = this.source_type;
            //   fb.root_name = this.get_use_name(); // .source_name;
            // }
            fb.ancestries = [...this.ancestries];
            // if (o.name === "Ability Score Increase Options") {
            //   console.log(o);
            //   console.log(fb);
            // }
            this.char_feature_bases.push(fb);
          }
        }
      }
      this.sub_char_feature_sources = [];
      if (obj.spellcasting) {
        this.spellcasting = new CFSSpellcasting(obj.spellcasting);
      } else if (obj.spellbook) {
        this.spellcasting = new CFSSpellcasting();
        this.spellcasting.spellbook = new CharacterSpellbook(obj.spellbook);
      }
    }
  }

  get name(): string {
    return this.get_use_name();
  }

  get data_type() {
    return this.source?.data_type??"";
  }

  get variant_of_id(): string {
    return this.source?.variant_of_id??"";
  }

  get variant_ids(): string[] {
    return this.source?.variant_ids??[];
  }

  get variant_group_id(): string {
    if (this.source) {
      return this.source.variant_group_id;
    }
    return "";
  }

  get_use_name(): string {
    let use_name = "";
    if (this.replace_name === "") {
      if (this.source) {
        use_name = this.source.use_name;
      }
    } else {
      return this.replace_name;
    }
    const name_bonuses = this.name_bonuses.join(" ");
    return name_bonuses.length > 0 ? name_bonuses + " " + use_name : use_name;
  }

  suggestions_for_level(level: number): string[] {
    let ids: string[] = [];
    this.suggested_spells.forEach(sug => {
      sug.suggestions.filter(o => o.level === level).forEach(level_sug => { 
        level_sug.suggestions.forEach(type_sug => {
          ids = [...ids,...type_sug.ids]; 
        });
      });
    });
    return ids;
  }

  suggestions_for_level_and_type(level: number, type: string): string[] {
    let ids: string[] = [];
    this.suggested_spells.forEach(sug => {
      sug.suggestions.filter(o => o.level === level).forEach(level_sug => { 
        level_sug.suggestions.filter(o => o.type === type).forEach(type_sug => {
          ids = [...ids,...type_sug.ids]; 
        });
      });
    });
    return ids;
  }

  get tags() {
    if (this.source instanceof FeatureSource) {
      return this.source.tags;
    }
    return [];
  }

  get description(): string {
    if (this.source) {
      return this.source.description;
    }
    return "";
  }

  get all_char_feature_bases(): CharacterFeatureBase[] {
    let all_char_feature_bases = this.char_feature_bases;
    for (const scfs of this.sub_char_feature_sources) {
      all_char_feature_bases = [...all_char_feature_bases, ...scfs.all_char_feature_bases];
    }
    return all_char_feature_bases;
  }

  get search_ids(): string[] {
    if (this.source) {
      return this.source.search_ids;
    }
    return [];
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      source_id: this.source_id,
      source_type: this.source_type,
      source_name: this.source_name,
      level: this.level,
      do_suggestions: this.do_suggestions,
      ancestries: this.ancestries,
      char_feature_bases: this.char_feature_bases.map(o => o.toDBObj()),
      spellcasting: this.spellcasting.toDBObj(),
    };
  }

  get feature_source_type() {
    const feature_source = this.source;
    if (feature_source) {
      if (feature_source instanceof MagicItemKeyword) {
        return "Item";
      }
      return feature_source.feature_source_type;
    } else {
      return "";
    }
  }

  get source_books(): { type: string, id: string, third_party: boolean }[] {
    return this.source?.source_books_old??[];
  }

  copy_source = async ({
    char, 
    feature_source, 
    parent_cf,
    parent_cfb,
    item_id = "",
    log_it = false,
    swapping_from_cfs
  }: {
    char: Character | ComplexCreatureInstance, 
    feature_source: FeatureSource | StandardFeatures, 
    parent_cf?: CharacterFeature,
    parent_cfb?: CharacterFeatureBase,
    item_id?: string,
    log_it?: boolean,
    swapping_from_cfs?: CharacterFeatureSource
  }) => {
    if (!feature_source) {
      debugger;
      return undefined;
    }
    // log_it = feature_source.name === "Paladin"; // instanceof Background;
    // console.log(this);
    // console.log(feature_source);
    this.source_type = feature_source.feature_source_type;
    this.source_name = feature_source.name;
    this.source_id = feature_source._id;
    // const root_finder = all_feature_sources.filter(o => o._id === root_id);
    const optional_feature_bases = feature_source instanceof FeatureSource ? await char_util.get_optional_features(feature_source) : [];
    if (log_it) {
      // debugger;
      // console.log(all_feature_sources)
      console.log(this);
      console.log(feature_source);
      // console.log(root_finder);
      // console.log(root_id);
    }
    if (swapping_from_cfs) {
      if (parent_cf) {
        this.ancestries = [...parent_cf.ancestries.map(o => { return {...o}; })];
        this.ancestries.push({ true_id: this.true_id, source_id: this.source_id, parent_id: parent_cf.source_true_id, type: this.source_type, name: this.source_name });
      } else {
        this.ancestries = [{ true_id: this.true_id, source_id: this.source_id, parent_id: "", type: this.source_type, name: this.source_name }];
      } 
    } else {
      if (parent_cf) {
        this.ancestries = [...parent_cf.ancestries.map(o => { return {...o}; })];
        this.ancestries.push({ true_id: this.true_id, source_id: this.source_id, parent_id: parent_cf.source_true_id, type: this.source_type, name: this.source_name });
      } else if (this.ancestries.length === 0) {
        this.ancestries.push({ true_id: this.true_id, source_id: this.source_id, parent_id: "", type: this.source_type, name: this.source_name });
      } 
    }
    if (parent_cfb) {
      this.parent_feature_base_level = parent_cfb.sort_level;
    }
    if (item_id !== "") {
      const item_finder = char.items.filter(o => o.true_id === item_id);
      if (item_finder.length === 1) {
        this.item_id = item_id;
        this.item_name = item_finder[0].name;
      }
    }
    if (!this.source || this.source._id !== feature_source._id) {
      this.source = feature_source;
    }
    // Removing add_sources because they will now be added on feature processing
    if (log_it) {
      console.log(this.char_feature_bases.length);  
      debugger;
    }
    const removing = this.char_feature_bases.filter(o => 
      !feature_source.feature_bases.some(o2 => 
        o.true_id.includes(o2.true_id)) && 
      !optional_feature_bases.some(o2 => 
        o.true_id.includes(o2.feature_base.true_id))
    );
    this.char_feature_bases = this.char_feature_bases.filter(o => 
      feature_source.feature_bases.some(o2 => 
        o.true_id.includes(o2.true_id)) || 
      optional_feature_bases.some(o2 => 
        o.true_id.includes(o2.feature_base.true_id))
    );
    if (log_it) {
      console.log(this.char_feature_bases.length);
      // debugger; 
    }
    for (const fb of feature_source.feature_bases) {
      let char_feature_base = this.char_feature_bases.find(o => 
        o.true_id === fb.true_id);
      if (log_it) {
        console.log(fb);
        console.log(char_feature_base);
        console.log(this.char_feature_bases);
      }
      if (char_feature_base) {
        if (log_it) {
          console.log(char_feature_base);
        }
        char_feature_base.copyFeatureBase(fb, 
          this, char, log_it);
        char_feature_base.source_id = this.source_id;
        if (log_it) {
          console.log(fb);
          console.log(char_feature_base);
        }
      } else {
        char_feature_base = new CharacterFeatureBase();
        char_feature_base.copyFeatureBase(fb, 
          this, char); //, log_it);
        char_feature_base.source_id = this.source_id;
        if (log_it) {
          console.log(fb);
          console.log(char_feature_base);
        }
        this.char_feature_bases.push(char_feature_base);
      }
      for (const repeat_level of fb.repeat_levels) {
        if (log_it) {
          debugger;
        }
        let repeat_cfb = this.char_feature_bases.find(o => 
          o.true_id === `${fb.true_id}_${repeat_level}`);
        if (log_it) {
          console.log(fb);
          console.log(repeat_cfb);
          console.log(this.char_feature_bases);
        }
        if (repeat_cfb) {
          if (log_it) {
            console.log(repeat_cfb);
          }
          repeat_cfb.copyFeatureBase(fb, 
            this, char, log_it, repeat_level);
          repeat_cfb.source_id = this.source_id;
          if (log_it) {
            console.log(fb);
            console.log(repeat_cfb);
          }
        } else {
          // It may be that it was on an old cf which has been replaced with a repeat_level.
          const removed_cfbs = removing.filter(o => o.level === repeat_level); 
          let removed_cfb: CharacterFeatureBase | undefined;
          for (const old_cfb of removed_cfbs) {
            // Look for one that has all the same features
            let good = true;
            for (const f of fb.features) {
              if (!old_cfb.features.some(o => o.ff_id === f.floating_feature_id)) {
                good = false;
                break;
              }
            }
            if (good) {
              removed_cfb = old_cfb;
              break;
            }
          }
          repeat_cfb = new CharacterFeatureBase();
          repeat_cfb.copyFeatureBase(fb, 
            this, char, log_it, repeat_level); 
          repeat_cfb.source_id = this.source_id;
          if (removed_cfb) {
            // Go through each of the features and copy the feature_options
            
            for (const cf of repeat_cfb.features) {
              const old_cf = removed_cfb.features.find(o => o.ff_id === cf.ff_id);
              if (old_cf) {
                cf.feature_options = old_cf.feature_options;
              }
            }
          }
          if (log_it) {
            console.log(fb);
            console.log(repeat_cfb);
          }
          this.char_feature_bases.push(repeat_cfb);
        }
      }
    }
    if (log_it) {
      console.log(this.char_feature_bases.length); 
      console.log(optional_feature_bases);
      console.log(this.char_feature_bases.filter(o => o.name.includes("Options")));
      this.char_feature_bases.filter(o => o.name.includes("Options")).forEach(cfb => {
        cfb.features.forEach(cf => {
          cf.feature_options.forEach(fo => {
            if (fo instanceof CharacterASIBaseFeature) {
              fo.asi_features.forEach((asif: CharacterASIFeature) => {
                console.log(asif.selected_option);
              });
            }
          });
        });
      });
    }
    for (const fb of optional_feature_bases) {
      await data_util.loadObject(fb);
      let char_feature_base = this.char_feature_bases.find(o => 
        o.true_id === fb.feature_base.true_id);
      if (char_feature_base) {
        if (log_it) {
          console.log(char_feature_base);
        }
        char_feature_base.copyFeatureBase(fb, 
          this, char, log_it);
        char_feature_base.source_id = this.source_id;
        if (log_it) {
          console.log(fb);
          console.log(char_feature_base);
        }
      } else {
        char_feature_base = new CharacterFeatureBase();
        char_feature_base.copyFeatureBase(fb, 
          this, char); //, log_it);
        char_feature_base.source_id = this.source_id;
        if (log_it) {
          console.log(fb);
          console.log(char_feature_base);
        }
        this.char_feature_bases.push(char_feature_base);
      }
    }
    if (log_it) {
      console.log(this.char_feature_bases.filter(o => o.name.includes("Options")));
      this.char_feature_bases.filter(o => o.name.includes("Options")).forEach(cfb => {
        cfb.features.forEach(cf => {
          cf.feature_options.forEach(fo => {
            if (fo instanceof CharacterASIBaseFeature) {
              fo.asi_features.forEach((asif: CharacterASIFeature) => {
                console.log(asif.selected_option);
              });
            }
          });
        });
      });
    }
  }

  clone(drop_ids = false): CharacterFeatureSource {
    const clone = new CharacterFeatureSource();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterFeatureSource, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.level = copyMe.level;
    this.do_suggestions = copyMe.do_suggestions;
    this.source_id = copyMe.source_id;
    this.source_type = copyMe.source_type;
    this.source_name = copyMe.source_name;
    this.ancestries = [...copyMe.ancestries.map(o => { return {...o}; })];
    // this.root_id = copyMe.root_id;
    // this.root_type = copyMe.root_type;
    // this.root_name = copyMe.root_name;
    this.item_id = copyMe.item_id;
    this.item_name = copyMe.item_name;
    this.source = copyMe.source;
    this.char_feature_bases = [...copyMe.char_feature_bases]; // .map(cfb => cfb.clone(drop_ids));
    this.sub_char_feature_sources = [...copyMe.sub_char_feature_sources]; // .map(scfs => scfs.clone(drop_ids));
    this.selectable_hash = {...copyMe.selectable_hash};
    this.selectable_types = {...copyMe.selectable_types};
    this.table = copyMe.table;
    this.spellcasting = copyMe.spellcasting.clone();
    for (const cfb of this.char_feature_bases) {
      // cfb.source_true_id = this.true_id;
      for (const cf of cfb.features) {
        cf.source_true_id = this.true_id;
      }
    }
    this.needs_attention = copyMe.needs_attention;
  }

  get root(): FeatureAncestry | undefined {
    return this.ancestries.filter(o => o.parent_id === "")[0];
  }

  get parent(): FeatureAncestry | undefined {
    const me = this.ancestries.filter(o => o.true_id === this.true_id)[0];
    if (me) {
      return this.ancestries.filter(o => o.true_id === me.parent_id)[0];
    }
    return undefined;
  }

  get root_id(): string {
    const root = this.root;
    if (root) {
      return root.source_id;
    } else {
      return this.source_id;
    }
  }

  get root_name(): string {
    const root = this.root;
    if (root) {
      return root.name;
    } else {
      return this.source_name;
    }
  }

  get root_type(): string {
    const root = this.root;
    if (root) {
      return root.type;
    } else {
      return this.source_type;
    }
  }
}

export class CFSSpellcasting {
  // Spellcasting
  spell_infusions = false; // If this is true, then it's Infusions instead
  spellcasting_ability: AbilityScoreType | "None" = "None";
  spell_dc = 0;
  spell_attack = 0;
  spellcasting_modifier = 0;
  spell_table = "";
  spell_level_max = 0;
  spellcasting_level = 0; // Full, Half, Third, etc
  // spellcasting_level_bonus = 0; // 1 for Artificer
  spell_list_ids: string[] = [];
  schools: string[] = [];
  spells: CharacterSpell[] = [];
  extra_spells: CharacterSpell[] = [];
  // spell_ids: string[] = [];
  // cantrip_ids: string[] = [];
  knowledge_type: "Prepared" | "Known" | "Spellbook" = "Known"; // Prepared, Known, Spellbook
  // These can be increased through other features.
  cantrips_max = 0;
  extra_cantrips = 0;
  // base_spell_count: number;
  // spell_count_per_level: number;
  // extra_prepared_from_ability: string; // This increases the number of spells Prepared/Known
  spells_prepared_max = 0; // This is calculated from the two above
  extra_spells_prepared = 0;
  max_infusions_per_item = 0;
  infusion_mik_id = "";
  // These can be granted through other features
  ritual_casting = false;
  spellcasting_focus = "";
  // These are only from other features
  spellbook: CharacterSpellbook | null = null;
  bonus_spells: CharacterBonusSpells[] = [];
  extra_spell_lists: ExtraSpellLists[] = [];
  modifying_spell: CharacterSpell | null = null;

  constructor(obj?: any) {
    if (obj) {
      this.spellbook = obj.spellbook ? new CharacterSpellbook(obj.spellbook) : null;
      this.extra_cantrips = obj.extra_cantrips ? obj.extra_cantrips : 0;
      this.extra_spells_prepared = obj.extra_spells_prepared ? obj.extra_spells_prepared : 0;
      this.spells = obj.spells ? obj.spells.map((o: any) => new CharacterSpell(o)) : [];
      this.extra_spells = obj.extra_spells ? obj.extra_spells.map((o: any) => new CharacterSpell(o)) : [];
    }
  }

  toDBObj(): any {
    return {
      spellbook: this.spellbook ? this.spellbook.toDBObj() : null,
      extra_cantrips: this.extra_cantrips,
      extra_spells_prepared: this.extra_spells_prepared,
      spells: this.spells.map(o => o.toDBObj()),
      extra_spells: this.extra_spells.map(o => o.toDBObj())
    };
  }

  clone(): CFSSpellcasting {
    const clone = new CFSSpellcasting();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CFSSpellcasting): void {
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.spell_dc = copyMe.spell_dc;
    this.spell_attack = copyMe.spell_attack;
    this.spellcasting_modifier = copyMe.spellcasting_modifier;
    this.spell_table = copyMe.spell_table;
    this.spell_level_max = copyMe.spell_level_max;
    this.spellcasting_level = copyMe.spellcasting_level;
    // this.spellcasting_level_bonus = copyMe.spellcasting_level_bonus;
    this.spell_list_ids = copyMe.spell_list_ids;
    this.schools = copyMe.schools;
    // this.spell_ids = [...copyMe.spell_ids];
    // this.cantrip_ids = [...copyMe.cantrip_ids];
    this.spells = copyMe.spells.map(o => o.clone());
    this.extra_spells = copyMe.extra_spells.map(o => o.clone());
    this.knowledge_type = copyMe.knowledge_type;
    this.cantrips_max = copyMe.cantrips_max;
    this.extra_cantrips = copyMe.extra_cantrips;
    this.spells_prepared_max = copyMe.spells_prepared_max;
    this.extra_spells_prepared = copyMe.extra_spells_prepared;
    this.ritual_casting = copyMe.ritual_casting;
    this.spellcasting_focus = copyMe.spellcasting_focus;
    this.spellbook = copyMe.spellbook ? copyMe.spellbook.clone() : null;
    this.bonus_spells = [...copyMe.bonus_spells];
    this.extra_spell_lists = [...copyMe.extra_spell_lists];
    // this.infusion_mik_id = copyMe.infusion_mik_id;
  }
}

export interface FeatureAncestry {
  true_id: string;
  source_id: string;
  type: string;
  name: string;
  parent_id: string;
}